/// <amd-module name="VSS/Features/FuzzySearch" />

///////////////////////////////////////////////////////////////////////////////////////
// Fuzzy filtering based on https://github.com/jeancroy/fuzzaldrin-plus
//
// The implementation is a conversion from coffeescript to typescript to allow customizations.
// Any improvements/fixes in external lib will require to be merged back to this library.
// The structure is retained to ease the merge back and forth from external lib
//
// There are three modules in the library:
//  1. Fuzzaldrin
//     Defines top level methods to perform scoring, matching and filtering of subject strings.
//     If existing scorer and matcher are required as it is, its recommended to use Fuzzaldrin
//     to filter, score and match the strings.
//     It also defines a top level FuzzyFilter that uses options like maximum positive candidates, maximum
//     results which are very useful in large dataset scenarios.
//     In addition to that, FuzzyFilter also provide performance statistics (last query execution).
//
//  2. Scorer
//     Contains methods that can be directly or indirectly (using Fuzzaldrin) consumed to perform
//     string scoring.
//
//  3. Matcher
//     Defines methods to perform matching a string against another string. It uses string alignment
//     and backtracking to return most suitable matching indices.The implementation is an
//     to improve over the leftmost alignment(match first available
//     matching characters), as characters occurring later in the string can be of more interest than
//     those occuring earlier.
//
export namespace Fuzzaldrin {
    // cache for match procedure
    let prepQueryCache: Scorer.Query;

    export function prepQuery(query: string, options: Scorer.QueryOptions): Scorer.Query {
        options = parseOptions(options);
        return Scorer.prepQuery(query, options);
    }

    /**
     * filter options
     */
    export interface filterOptions {
        /**
         * Candidates are expected to be array of string,
         * if not provide key to retrieve candidate's string
         *
         * Example {item: "candidatestring"} // key: "item"
         */

        key?: string;

        /**
         * maximum number of results to return, default to all
         */
        maxResults?: number;

        /**
         * maximum number of positive candidates to collect, disabled by default
         * can be used with large data set to calculate only X number of positives
         */
        maxInners?: number;

        /**
         * compute additional score for file paths, Enabled by default
         */
        isPath?: boolean;

        /**
         * compute bonus score for file extension match, Enabled by default
         */
        useExtensionBonus?: boolean;

        /**
         * characters that are optional in query, defaults to Scorer.opt_char_re
         */
        optCharRegEx?: string | RegExp;

        /**
         * path seperator if candidates are file paths, defaults to "/"
         */

        pathSeparator?: string;
    }

    function parseOptions(options?: filterOptions): filterOptions {
        let parsedOptions = options || {};

        // set defaults
        if (parsedOptions.isPath == null) {
            parsedOptions.isPath = true;
        }

        if (parsedOptions.useExtensionBonus == null) {
            parsedOptions.useExtensionBonus = true;
        }

        if (parsedOptions.pathSeparator == null) {
            parsedOptions.pathSeparator = Scorer.defaultPathSeparator;
        }

        if (parsedOptions.optCharRegEx == null) {
            parsedOptions.optCharRegEx = Scorer.opt_char_re;
        }
        return parsedOptions;
    }

    //////////////////////////////////////////////////
    // Fuzzy filter
    export class FuzzyFilter<T> {
        private _lastQueryMatchElapsed: number;
        private _lastQueryScoreElapsed: number;
        private _lastQuery: string;
        private _options: filterOptions;

        constructor(options?: filterOptions) {
            this._options = parseOptions(options);
        }

        public setOptions(options?: filterOptions) {
            this._options = parseOptions(options);
        }

        /**
         * Filter candidates based on simlarity with query string
         * candidates are ranked from high to low based on score
         *
         * @param candidates
         * @param query
         */
        public filterCandidates(candidates: T[], query: string): T[] {
            // check parameters
            if (!(query && query.length && candidates && candidates.length)) {
                return [];
            }

            let scoredCandidates: Scorer.ScoredCandidate<T>[] = [];
            let matchedCandidates: T[] = [];

            // get options
            const { key, maxResults, maxInners, isPath, useExtensionBonus, optCharRegEx, pathSeparator } = this._options;

            // allowed positive candidates
            let spotLeft = candidates.length;
            if (maxInners && maxInners > 0) {
                spotLeft = maxInners;
            }

            // check if key is specified
            const isKey = key !== null;

            let queryObj = new Scorer.Query(query, this._options);
            this._lastQuery = query;

            // clear last stats
            this._lastQueryMatchElapsed = this._lastQueryScoreElapsed = 0;

            // matching
            let matchStart = performance.now();
            for (let candidate of candidates) {
                let str = isKey ? (candidate as any)[key!] : candidate;
                if (!str) {
                    continue;
                }

                if (Scorer.isMatch(str, queryObj.core_lw, queryObj.core_up)) {
                    matchedCandidates.push(candidate);
                    if (--spotLeft === 0) {
                        break;
                    }
                }
            }

            // calculate match time for last query
            this._lastQueryMatchElapsed = Math.round(performance.now() - matchStart);

            // scoring
            let scoreStart = performance.now();
            for (let candidate of matchedCandidates) {
                let str = isKey ? (candidate as any)[key!] : candidate;
                if (!str) {
                    continue;
                }
                let score = Scorer.score(str, query, queryObj, isPath, useExtensionBonus, pathSeparator);
                if (score > 0) {
                    scoredCandidates.push({ candidate: candidate, score: score });
                }
            }

            // calculate scoring time for last query
            this._lastQueryScoreElapsed = Math.round(performance.now() - scoreStart);

            //Sort scores in descending order
            scoredCandidates.sort(this._candidateComparer);
            candidates = scoredCandidates.map(scoredCandidate => scoredCandidate.candidate);

            // if max result is set
            if (!!maxResults) {
                candidates = candidates.slice(0, maxResults);
            }

            return candidates;
        }

        /**
         * Get run time stats for last filter execution.
         * Deterministic operations like candidate comparison are not considered in execution stats.
         * They are O(n) operation. where n = candidates count
         */
        public getLastQueryExecutionStats(): { query: string; matchTime: number; scoreTime: number } {
            return {
                query: this._lastQuery,
                matchTime: this._lastQueryMatchElapsed,
                scoreTime: this._lastQueryScoreElapsed
            };
        }

        private _candidateComparer(first: Scorer.ScoredCandidate<T>, second: Scorer.ScoredCandidate<T>): number {
            return second.score - first.score;
        }
    }

    /**
     * Score subject string against query
     * @param subject subject string
     * @param query Query string
     * @param [prepQuery] Optional query
     * @param [options] filter options
     *
     * @return Array<number> matching indexes
     */
    export function score(subject: string, query: string, prepQuery?: Scorer.Query, options?: filterOptions): number {
        if (!(subject && query && subject.length && query.length)) {
            return 0;
        }

        options = parseOptions(options);

        const { isPath, useExtensionBonus, optCharRegEx, pathSeparator } = options;

        // if prepQuery is given, use it, else if prepQueryCache match the same query -> use cache, else -> compute & cache
        if (prepQuery == null) {
            prepQuery =
                prepQueryCache && prepQueryCache.query === query
                    ? prepQueryCache
                    : (prepQueryCache = new Scorer.Query(query, { optCharRegEx: optCharRegEx, pathSeparator: pathSeparator }));
        }

        return Scorer.score(subject, query, prepQuery, isPath, useExtensionBonus, pathSeparator);
    }

    /**
     * Match subject string with query using sequence alignment
     * @param subject subject string
     * @param query Query string
     * @param [prepQuery] Optional query
     * @param [options] filter options
     *
     * @return Array<number> matching indexes
     */
    export function match<T>(subject: T, query: string, prepQuery?: Scorer.Query, options?: filterOptions): number[] {
        if (!(subject && query)) {
            return [];
        }

        const match_options = parseOptions(options);
        const { key, isPath, useExtensionBonus, optCharRegEx, pathSeparator } = match_options;

        // check if key is specified
        const isKey = key != null;
        let subject_string = isKey ? (subject as any)[key!] : subject;

        if (subject_string === query) {
            // return all indexes
            return Array.apply(null, { length: subject_string.length } as any[]).map((value, index) => {
                return index;
            });
        }

        // if prepQuery is given -> use it, else if prepQueryCache match the same query -> use cache, else -> compute & cache
        if (prepQuery == null) {
            prepQuery =
                prepQueryCache && prepQueryCache.query === query
                    ? prepQueryCache
                    : (prepQueryCache = new Scorer.Query(query, { optCharRegEx: optCharRegEx, pathSeparator: pathSeparator } as Scorer.QueryOptions));
        }

        const string_lw = subject_string.toLocaleLowerCase();

        if (!Scorer.isMatch(subject_string, prepQuery.core_lw, prepQuery.core_up)) {
            return [];
        }

        // Full path results
        let matches = Matcher.match(subject_string, string_lw, prepQuery);

        // if there is no matches on the full path, there should not be any on the base path either.
        if (matches.length === 0) {
            return matches;
        }

        // Is there a base path ?
        if (subject_string.indexOf(pathSeparator) > -1) {
            // Base path results
            const baseMatches = Matcher.basenameMatch(subject_string, string_lw, prepQuery, pathSeparator!);

            // Combine the results, removing duplicate indexes
            matches = Matcher.mergeMatches(matches, baseMatches);
        }
        return matches;
    }
}

export namespace Scorer {
    //=====================================================================================================================
    //
    // Score similarity between two string
    //
    //  isMatch: Fast detection if all character of needle is in haystack
    //  score: Find string similarity using a Smith Waterman algorithm
    //         Modified to account for programing scenarios (CamelCase folder/file.ext object.property)
    //
    // Copyright (C) 2015 Jean Christophe Roy and contributors
    // MIT License: http://opensource.org/licenses/MIT

    export const defaultPathSeparator = "/";

    // Base point for a single character match
    // This balance making patterns VS position and size penalty.
    const wm = 150;

    //Fading function
    const pos_bonus = 20; // The character from 0..pos_bonus receive a greater bonus for being at the start of string.
    const tau_depth = 13; // Directory depth at which the full path influence is halved.
    const tau_size = 85; // Full path length at which the whole match score is halved.
    const file_coeff = 1.2; // Full path is also penalized for length of basename. This adjust a scale factor for that penalty.

    // Miss count
    // When subject[i] is query[j] we register a hit.
    // Limiting hit put a boundary on how many permutation we consider to find the best one.
    // Helps to speed-up processing of long path and query containing frequent character (eg vowels)
    //
    // If a spec with frequent repetition fail, increase this.
    // This has a direct influence on worst case scenario benchmark.
    const miss_coeff = 0.75; //Max number missed consecutive hit = ceil(miss_coeff*query.length) + 5

    //
    // Optional chars
    // Those char improve the score if present, but will not block the match (score=0) if absent.

    export const opt_char_re = /[ _\-:\/\\]/g;

    export function coreChars(query: string, optCharRegEx: any = opt_char_re): string {
        return query.replace(optCharRegEx, "");
    }

    //
    // Truncated Upper Case:
    // --------------------
    //
    // A fundamental mechanic is that we are able to keep uppercase and lowercase variant of the strings in sync.
    // For that we assume uppercase and lowercase version of the string have the same length. Of course unicode being unicode there's exceptions.
    // See ftp://ftp.unicode.org/Public/UCD/latest/ucd/SpecialCasing.txt for the list
    //
    // "Straße".toLocaleUpperCase() -> "STRASSE"
    // truncatedUpperCase("Straße") -> "STRASE"
    // iterating over every character, getting uppercase variant and getting first char of that.
    //
    function truncatedUpperCase(str: string): string {
        let upper = "";
        for (let i = 0, len = str.length; i < len; i++) {
            upper += str[i].toLocaleUpperCase()[0];
        }
        return upper;
    }

    //
    // Count number of folder in a path. Used for score when subject strings are paths
    // (consecutive slashes count as a single directory)
    //
    function countDir(path: string, end: number, pathSeparator: string): number {
        if (end < 1) {
            return 0;
        }

        let count = 0;
        let i = -1;

        //skip slash at the start so `foo/bar` and `/foo/bar` have the same depth.
        while (++i < end && path[i] === pathSeparator);

        while (++i < end) {
            if (path[i] === pathSeparator) {
                count++; //record first slash, but then skip consecutive ones
                while (++i < end && path[i] === pathSeparator);
            }
        }

        return count;
    }

    /**
     * query options
     */
    export interface QueryOptions {
        optCharRegEx?: string | RegExp;
        pathSeparator?: string;
    }

    /**
     * Query object
     * Allow to reuse some quantities computed from query.
     * Optional char can optionally be specified in the form of a regular expression.
     */
    export class Query {
        private _options: QueryOptions;
        /**
         * query string as is
         */
        public query: string;

        /**
         * query string (lower case)
         */
        public query_lw: string;

        /**
         * core query with truncated optional characters
         */
        public core: string;

        /**
         * core string (lowercase)
         */
        public core_lw: string;

        /**
         * core string (uppercase)
         */
        public core_up: string;

        /**
         * directory count in query string
         */
        public depth: number;

        /**
         * file extension if provided in query, (.ts, .cs etc)
         */
        public ext: string;

        /**
         * Array map of character codes in query
         * enables fast lookup
         */
        public charCodes: boolean[];

        // ctor
        constructor(query: string, options?: QueryOptions) {
            if (!query || query.length == 0) {
                return;
            }

            this._options = options || { optCharRegEx: opt_char_re, pathSeparator: defaultPathSeparator };
            this.query = query;
            this.query_lw = query.toLocaleLowerCase();
            this.core = coreChars(query, this._options.optCharRegEx);
            this.core_lw = this.core.toLocaleLowerCase();
            this.core_up = truncatedUpperCase(this.core);
            this.depth = this._options.pathSeparator ? countDir(query, query.length, this._options.pathSeparator) : 0;
            this.ext = this.getExtension(this.query_lw);
            this.charCodes = [];

            // compute hashmap for character codes in query
            const len = this.query_lw.length;
            let i = -1;
            while (++i < len) {
                let charCode = this.query_lw.charCodeAt(i);
                this.charCodes[charCode] = true;
            }
        }

        /**
         * Find fraction of extension that is matched by query.
         * For example mf.h prefers myFile.h to myfile.html
         * This need special handling because it give point for not having characters (the `tml` in above example)
         * @param str
         */
        private getExtension(str: string): string {
            let pos = str.lastIndexOf(".");
            return pos < 0 ? "" : str.substr(pos + 1);
        }
    }

    /**
     * Prepare Query
     */
    export function prepQuery(query: string, options: QueryOptions): Query {
        return new Query(query, options);
    }

    export class AcronymResult {
        constructor(public score: number, public pos: number, public count: number) {}
    }

    let emptyAcronymResult = new AcronymResult(0, 0, 0);

    // Scored candidate
    export interface ScoredCandidate<T> {
        candidate: T;
        score: number;
    }

    /**
    * Manage the logic of testing if there's a match and calling the main scoring function
    * Also manage scoring a path and optional character.

    * @return score for the candidateString
    */

    export function score(
        candidateString: string,
        query: string,
        prepQuery?: Query,
        isPath?: boolean,
        useExtensionBonus?: boolean,
        pathSeparator?: string
    ): number {
        if (!candidateString || !query) {
            return 0;
        }

        let string_lw = candidateString.toLocaleLowerCase();

        if (!prepQuery) {
            prepQuery = new Query(query);
        }

        let score = scoreMain(candidateString, string_lw, prepQuery);

        if (isPath) {
            score = scorePath(candidateString, string_lw, prepQuery, score, useExtensionBonus!, pathSeparator!);
        }

        return Math.ceil(score);
    }

    /**
     * Main scoring algorithm, doesn't use options
     */
    export function scoreMain(subject: string, subject_lw: string, prepQuery: Query): number {
        const query = prepQuery.query;
        const query_lw = prepQuery.query_lw;

        let s_len = subject ? subject.length : 0;
        let q_len = query ? query.length : 0;

        if (!(s_len && q_len)) {
            return 0;
        }

        //----------------------------
        // Abbreviations sequence

        const acro = scoreAcronyms(subject, subject_lw, query, query_lw);
        let acro_score = acro.score;

        // Whole query is abbreviation ?
        // => use that as score
        if (acro.count === q_len) {
            return scoreExact(q_len, s_len, acro_score, acro.pos);
        }

        //----------------------------
        // Exact Match ?
        // => use that as score

        let pos = subject_lw.indexOf(query_lw);
        if (pos > -1) {
            return scoreExactMatch(subject, subject_lw, query, query_lw, pos, q_len, s_len);
        }

        //----------------------------
        // Individual characters
        // (Smith Waterman algorithm)

        //Init
        let score_row = new Array(q_len);
        let csc_row = new Array(q_len);
        let sz = scoreSize(q_len, s_len);

        const miss_budget = Math.ceil(miss_coeff * q_len) + 5;
        let miss_left = miss_budget;

        //Fill with 0
        let j = -1;

        // initialize score and consicutive score rows to ZERO
        while (++j < q_len) {
            score_row[j] = 0;
            csc_row[j] = 0;
        }

        //TODO: Check if speedup is important on average.
        //This assume first and last char are correct. Possibly breaking some allowError score.

        // Limit the search to the active region
        // for example with query `abc`, subject `____a_bc_ac_c____`
        // there's a region before first `a` and after last `c`
        // that can be simplified out of the matching process

        // Before first occurrence in subject of first letter of query, or -1
        let i = subject_lw.indexOf(query_lw[0]);
        if (i > -1) {
            i--;
        }

        // After last occurrence of last letter of query,
        let mm = subject_lw.lastIndexOf(query_lw[q_len - 1], s_len);

        if (mm > i) {
            s_len = mm + 1;
        }

        let csc_invalidated = true;
        let score = 0;
        while (++i < s_len) {
            //foreach char si of subject
            let si_lw = subject_lw.charCodeAt(i);

            // only if si_lw is in string evaluate score otherwise continue
            if (!prepQuery.charCodes[si_lw]) {
                if (!csc_invalidated) {
                    j = -1;
                    while (++j < q_len) {
                        csc_row[j] = 0;
                    }
                    csc_invalidated = true;
                }
                continue;
            }

            score = 0;
            let score_diag = 0;
            let csc_diag = 0;
            let record_miss = true;
            csc_invalidated = false;
            j = -1; //0..n-1
            while (++j < q_len) {
                //foreach char qj of query

                // What is the best gap ?
                // score_up contain the score of a gap in subject.
                // score_left = last iteration of score, -> gap in query.
                let score_up = score_row[j];
                if (score_up > score) {
                    score = score_up;
                }

                //Reset consecutive
                let csc_score = 0;
                let qj_lw = query_lw.charCodeAt(j);
                //Compute a tentative match
                if (qj_lw === si_lw) {
                    let start = isWordStart(i, subject, subject_lw);

                    // Forward search for a sequence of consecutive char
                    csc_score = csc_diag > 0 ? csc_diag : scoreConsecutives(subject, subject_lw, query, query_lw, i, j, start);

                    // Determine bonus for matching A[i] with B[j]
                    let align = score_diag + scoreCharacter(i, start, acro_score, csc_score);

                    //Are we better using this match or taking the best gap (currently stored in score)?
                    if (align > score) {
                        score = align;
                        // reset consecutive missed hit count
                        miss_left = miss_budget;
                    } else {
                        // We rejected this match and record a miss.
                        // If budget is exhausted exit
                        // Each character of query have it's score history stored in score_row
                        // To get full query score use last item of row.
                        if (record_miss && --miss_left <= 0) {
                            return score_row[q_len - 1] * sz;
                        }
                        record_miss = false;
                    }
                }
                //Prepare next sequence & match score.
                score_diag = score_up;
                csc_diag = csc_row[j];
                csc_row[j] = csc_score;
                score_row[j] = score;
            }
        }
        // pick latest score
        score = score_row[q_len - 1];
        return score * sz;
    }

    /**
     * Scoring adjustment for path, runs only if candidates are path strings
     */
    export function scorePath(
        subject: string,
        subject_lw: string,
        prepQuery: Query,
        fullPathScore: number,
        useExtensionBonus: boolean,
        pathSeparator: string
    ): number {
        if (fullPathScore === 0) {
            return 0;
        }

        // skip trailing slashes
        let end = subject.length - 1;
        while (subject[end] === pathSeparator) {
            end--;
        }

        // Get position of basePath of subject
        let basePos = subject.lastIndexOf(pathSeparator, end);
        const fileLength = end - basePos;

        // Get a bonus for matching extension
        let extAdjust = 1.0;
        if (useExtensionBonus) {
            extAdjust += getExtensionScore(subject_lw, prepQuery.ext, basePos, end, 2);
            fullPathScore *= extAdjust;
        }

        // no basePath, nothing else to compute
        if (basePos === -1) {
            return fullPathScore;
        }

        // Get the number of folder in query
        let depth = prepQuery.depth;

        // Get that many folder from subject
        while (basePos > -1 && depth-- > 0) {
            basePos = subject.lastIndexOf(pathSeparator, basePos - 1);
        }

        // Get basePath score, if BaseName is the whole string, no need to recompute
        // We still need to apply the folder depth and filename penalty.
        let subject_debase: string = subject.slice(basePos + 1, end + 1);
        let basePathScore = basePos === -1 ? fullPathScore : extAdjust * scoreMain(subject_debase, subject_debase.toLocaleLowerCase(), prepQuery);

        // Final score is linear interpolation between base score and full path score.
        // For low directory depth, interpolation favor base Path then include more of full path as depth increase
        //
        // A penalty based on the size of the basePath is applied to fullPathScore
        // That way, more focused basePath match can overcome longer directory path.
        let alpha = (0.5 * tau_depth) / (tau_depth + countDir(subject, end + 1, pathSeparator));
        return alpha * basePathScore + (1 - alpha) * fullPathScore * scoreSize(0, file_coeff * fileLength);
    }

    /**
     * Test whether there's a 1:1 relationship between query and acronym of candidate.
     * For that to happens
     * (a) All character of query must be matched to an acronym of candidate
     * (b) All acronym of candidate must be matched to a character of query.
     *
     * This method check for (b) assuming (a) has been checked before entering.
     */
    function isAcronymFullWord(subject: string, subject_lw: string, query: string, nbAcronymInQuery: number): boolean {
        const s_len = subject.length;
        const q_len = query.length;
        let count = 0;

        // Heuristic:
        // Assume one acronym every (at most) 12 character on average
        // This filter out long paths, but then they can match on the filename.
        if (s_len > 12 * q_len) {
            return false;
        }

        let s_index = -1;
        while (++s_index < s_len) {
            //For each char of subject
            //Test if we have an acronym, if so increase acronym count.
            //If the acronym count is more than nbAcronymInQuery (number of non separator char in query)
            //Then we do not have 1:1 relationship.
            if (isWordStart(s_index, subject, subject_lw) && ++count > nbAcronymInQuery) {
                return false;
            }
        }

        return true;
    }

    /**
     * Shared scoring logic between exact match, consecutive & acronym
     * Ensure pattern length dominate the score then refine to take into account case-sensitivity
     * and structural quality of the pattern on the overall string (word boundary)
     */
    export function scorePattern(matchCount: number, len: number, sameCaseCount: number, isStartOfWord: boolean, isEndOfWord: boolean): number {
        let sz = matchCount,
            bonus = 6; // to ensure consecutive length dominate score, this should be as large other bonus combined

        if (sameCaseCount === matchCount) {
            bonus += 2;
        }
        if (isStartOfWord) {
            bonus += 3;
        }
        if (isEndOfWord) {
            bonus += 1;
        }
        if (matchCount === len) {
            // when we match 100% of query we allow to break the size ordering.
            // This is to help exact match bubble up vs size, depth penalty etc
            if (isStartOfWord) {
                if (sameCaseCount === len) {
                    sz += 2;
                } else {
                    sz += 1;
                }
            }
            if (isEndOfWord) {
                bonus += 1;
            }
        }
        return sameCaseCount + sz * (sz + bonus);
    }

    /**
     * Compute score of an exact match at a position in subject
     */
    export function scoreExactMatch(
        subject: string,
        subject_lw: string,
        query: string,
        query_lw: string,
        position: number,
        query_len: number,
        subject_len: number
    ): number {
        // Test for word start
        let isStartofWord = isWordStart(position, subject, subject_lw);

        // Heuristic
        // If not a word start, test next occurrence
        // - We want exact match to be fast
        // - For exact match, word start has the biggest impact on score.
        // - Testing 2 instances is somewhere between testing only one and testing every instances.

        if (!isStartofWord) {
            let pos2 = subject_lw.indexOf(query_lw, position + 1);
            if (pos2 > -1) {
                isStartofWord = isWordStart(pos2, subject, subject_lw);
                if (isStartofWord) {
                    position = pos2;
                }
            }
        }

        //Exact case bonus.
        let i = -1;
        let sameCase = 0;
        while (++i < query_len) {
            if (query[position + i] === subject[i]) {
                sameCase++;
            }
        }

        let end = isWordEnd(position + query_len - 1, subject, subject_lw, subject_len);

        return scoreExact(query_len, subject_len, scorePattern(query_len, query_len, sameCase, isStartofWord, end), position);
    }

    /**
     * Calculate score for file extension, if any
     */
    function getExtensionScore(candidate: string, ext: string, startPos: number, endPos: number, maxDepth: number): number {
        // startPos is the position of last slash of candidate, -1 if absent.
        if (!ext.length) {
            return 0;
        }

        // Check that (a) extension exist, (b) it is after the start of the basename
        let pos = candidate.lastIndexOf(".", endPos);
        if (!(pos > startPos)) {
            return 0;
        }

        let ext_len = ext.length;
        let m = endPos - pos;

        // n contain the smallest of both extension length, m the largest.
        if (m < ext_len) {
            ext_len = m;
            m = ext.length;
        }

        //place cursor after dot & count number of matching characters in extension
        pos++;
        let matched = -1;
        while (++matched < ext_len) {
            if (candidate[pos + matched] !== ext[matched]) {
                break;
            }
        }

        // if nothing found, try deeper for multiple extensions, with some penalty for depth
        if (matched === 0 && maxDepth > 0) {
            return 0.9 * getExtensionScore(candidate, ext, startPos, pos - 2, maxDepth - 1);
        }

        // cannot divide by zero because m is the largest extension length and we return if either is 0
        return matched / m;
    }

    /**
     *
     * isMatch:
     * Are all (non optional)characters of query in subject, in proper order ?
     * Assumes non-unicode strings
     * @param subject Subject string
     * @param query_lw Query string lowercase
     * @param query_up Query string uppercase
     */
    export function isMatch(subject: string, query_lw: string, query_up: string): boolean {
        let s_len = subject.length;
        let q_len = query_lw.length;

        if (!s_len || q_len > s_len) {
            return false;
        }

        let s_index = -1;
        let q_index = -1;
        let qj_lw, qj_up, si;
        //foreach char of query
        while (++q_index < q_len) {
            qj_lw = query_lw.charCodeAt(q_index);
            qj_up = query_up.charCodeAt(q_index);

            // continue walking the subject from where we have left with previous query char
            // until we have found a character that is either lowercase or uppercase query.
            while (++s_index < s_len) {
                si = subject.charCodeAt(s_index);
                if (si === qj_lw || si === qj_up) {
                    break;
                }
            }
            // if we passed the last char, query is not in subject
            if (s_index === s_len) {
                return false;
            }
        }
        //Found every char of query in subject in proper order, match is positive
        return true;
    }

    /**
     * forward search for a sequence of consecutive character
     * @param subject Subject string
     * @param subject_lw Subject string(lowercase)
     * @param query Query string
     * @param query_lw Query string(lowercase)
     * @param s_start starting index in subject string
     * @param q_start starting index in query string
     * @param startOfWord
     * @return score for consecutive characters
     */
    export function scoreConsecutives(
        subject: string,
        subject_lw: string,
        query: string,
        query_lw: string,
        s_start: number,
        q_start: number,
        startOfWord: boolean
    ): number {
        const s_len = subject.length;
        const q_len = query.length;

        let s_count = s_len - s_start;
        let q_count = q_len - q_start;

        // check for minimum length to scan
        let min = s_count < q_count ? s_count : q_count;

        let sameCase = 0;
        let sz = 0; //sz will be one more than the last q_start is s_start

        // query_lw[q_start] is subject_lw[s_start] has been checked before entering now do case sensitive check.
        if (query[q_start] === subject[s_start]) {
            sameCase++;
        }

        //Continue while lowercase char are the same, record when they are case-sensitive match.
        while (++sz < min && query_lw[++q_start] === subject_lw[++s_start]) {
            if (query[q_start] === subject[s_start]) {
                sameCase++;
            }
        }

        // Faster path for single match.
        // Isolated character match occurs often and are not really interesting.
        // Fast path so we don't compute expensive pattern score on them.
        // Acronym should be addressed with acronym context bonus instead of consecutive.

        if (sz === 1) {
            return 1 + 2 * sameCase;
        }
        return scorePattern(sz, q_len, sameCase, startOfWord, isWordEnd(s_start, subject, subject_lw, s_len));
    }

    /**
     * Score subject against query based on acronym match
     *
     * @param subject subject string
     * @param subject_lw subject string (lowercase)
     * @param query query string
     * @param query_lw query string (lowercase)
     */
    export function scoreAcronyms(subject: string, subject_lw: string, query: string, query_lw: string): AcronymResult {
        const s_len = subject.length;
        const q_len = query.length;

        //a single char is not an acronym
        if (s_len < 1 || q_len < 1) {
            return emptyAcronymResult;
        }

        let count = 0;
        let sepCount = 0;
        let sumPos = 0;
        let sameCase = 0;

        let s_index = -1;
        let q_index = -1;

        //foreach char of query
        while (++q_index < q_len) {
            let q_lw = query_lw[q_index];

            // Separator will not score point but will continue the prefix when present.
            // Test that the separator is in the candidate and advance cursor to that position.
            // If no separator break the prefix
            if (isSeparator(q_lw)) {
                s_index = subject_lw.indexOf(q_lw, s_index + 1);
                if (s_index > -1) {
                    sepCount++;
                    continue;
                } else {
                    break;
                }
            }

            // For other characters we search for the first match where subject[i] = query[j]
            // that also happens to be a start-of-word
            while (++s_index < s_len) {
                if (q_lw === subject_lw[s_index] && isWordStart(s_index, subject, subject_lw)) {
                    if (query[q_index] === subject[s_index]) {
                        sameCase++;
                    }
                    sumPos += s_index;
                    count++;
                    break;
                }
            }
            // All of subject is consumed, stop processing the query.
            if (s_index === s_len) {
                break;
            }
        }

        // Here, all of query is consumed (or we have reached a character not in acronym)
        // A single character is not an acronym (also prevent division by 0)
        if (count < 2) {
            return emptyAcronymResult;
        }

        // Acronym are scored as start-of-word
        // Unless the acronym is a 1:1 match with candidate then it is upgraded to full-word.
        let fullWord = count === q_len ? isAcronymFullWord(subject, subject_lw, query, count) : false;

        let score = scorePattern(count, q_len, sameCase, true, fullWord);

        return new AcronymResult(score, sumPos / count, count + sepCount);
    }

    /**
     * Compute the bonuses for two chars that are confirmed to matches in a case-insensitive way
     * @param index position of match in subject string
     * @param startOfWord is start of word?
     * @param acro_score acronym match score
     * @param csc_score consecutive match score
     */
    export function scoreCharacter(index: number, startOfWord: boolean, acro_score: number, csc_score: number): number {
        let posBonus;
        // start of string / position of match bonus
        posBonus = scorePosition(index);

        // match IS a word boundary
        // choose between taking part of consecutive characters or consecutive acronym
        if (startOfWord) {
            return posBonus + wm * ((acro_score > csc_score ? acro_score : csc_score) + 10);
        }

        // normal Match
        return posBonus + wm * csc_score;
    }

    export function isWordStart(pos: number, subject: string, subject_lw: string): boolean {
        if (pos === 0) {
            // match is FIRST char ( place a virtual token separator before first char of string)
            return true;
        }

        let curr_s = subject[pos];
        let prev_s = subject[pos - 1];
        return (
            isSeparator(prev_s) || // match FOLLOW a separator
            (curr_s !== subject_lw[pos] && prev_s === subject_lw[pos - 1])
        ); // match is Capital in camelCase (preceded by lowercase)
    }

    export function isWordEnd(pos: number, subject: string, subject_lw: string, len: number): boolean {
        if (pos === len - 1) {
            // last char of string
            return true;
        }

        let curr_s = subject[pos];
        let next_s = subject[pos + 1];
        return (
            isSeparator(next_s) || // match IS FOLLOWED BY a separator
            (curr_s === subject_lw[pos] && next_s !== subject_lw[pos + 1])
        ); // match is lowercase, followed by uppercase
    }

    export function isSeparator(c: string): boolean {
        return c === " " || c === "." || c === "-" || c === "_" || c === "/" || c === "\\";
    }

    /**
     * Scoring helpers
     */

    /**
     * score a position
     */
    function scorePosition(position: number): number {
        let score;
        if (position < pos_bonus) {
            score = pos_bonus - position;
            return 100 + score * score;
        } else {
            return Math.max(100 + pos_bonus - position, 0);
        }
    }

    /**
     * Score based on candidate length and query length
     * @param query_len
     * @param subject_len
     */
    function scoreSize(query_len: number, subject_len: number): number {
        return tau_size / (tau_size + Math.abs(subject_len - query_len));
    }

    /**
     * Score exact matching (all query characters are present in subject string)
     * @param n
     * @param m
     * @param quality
     * @param pos
     */
    function scoreExact(query_len: number, subject_len: number, quality: number, pos: number): number {
        return 2 * query_len * (wm * quality + scorePosition(pos)) * scoreSize(query_len, subject_len);
    }
}

export namespace Matcher {
    // A match list is an array of indexes to characters that match.
    // This module should closely follow `Scorer` except that it returns an array
    // of indexes instead of a score.

    export function basenameMatch(subject: string, subject_lw: string, prepQuery: Scorer.Query, pathSeparator: string) {
        // Skip trailing slashes
        let end = subject.length - 1;
        while (subject[end] === pathSeparator) {
            end--;
        }

        // Get position of basePath of subject.
        let basePos = subject.lastIndexOf(pathSeparator, end);

        //If no PathSeparator, no base path exist.
        if (basePos === -1) {
            return [];
        }

        // Get the number of folder in query
        let depth = prepQuery.depth;

        // Get that many folder from subject
        while (depth-- > 0) {
            basePos = subject.lastIndexOf(pathSeparator, basePos - 1);
            if (basePos === -1) {
                //consumed whole subject ?
                return [];
            }
        }
        // Get basePath match
        basePos++;
        end++;
        return match(subject.slice(basePos, end), subject_lw.slice(basePos, end), prepQuery, basePos);
    }

    //
    // Merge two matches result and remove duplicate
    // (Assume sequences are sorted, matches are sorted by construction.)
    //
    export function mergeMatches(first: number[], second: number[]): number[] {
        let firstlen = first.length;
        let secondlen = second.length;

        if (secondlen === 0) {
            return first.slice();
        }
        if (firstlen === 0) {
            return second.slice();
        }

        let firstIndex = -1;
        let secondIndex = 0;
        let second_item = second[secondIndex];

        let merge: number[] = [];

        // Iterate over first array
        while (++firstIndex < firstlen) {
            let first_item = first[firstIndex];

            // push items in second array that are less than current item in first array
            while (second_item <= first_item && ++secondIndex < secondlen) {
                if (second_item < first_item) {
                    merge.push(second_item);
                }

                second_item = second[secondIndex];
            }
            merge.push(first_item);
        }

        // push remaining indexes in second array
        while (secondIndex < secondlen) {
            merge.push(second[secondIndex++]);
        }

        return merge;
    }

    /**
     * Align sequence (used for matching/highlighting)
     * Return position of subject characters that matches query.

     * Follow closely scorer.ScoreMain.
     * Except at each step we record what triggered the best score.
     * Then we trace back to output matched characters.

     * Differences are:
     * - we record the best move at each position in a matrix, and finish by a traceback.
     * - we reset consecutive sequence if we do not take the match.
     * - no hit miss limit

     * @param subject
     * @param subject_lw
     * @param prepQuery
     * @param offset
     */
    export function match(subject: string, subject_lw: string, prepQuery: Scorer.Query, offset: number = 0) {
        let query = prepQuery.query;
        let query_lw = prepQuery.query_lw;

        let slen = subject.length;
        let qlen = query.length;

        // this is like the consecutive bonus, but for camelCase / snake_case initials
        let acro_score = Scorer.scoreAcronyms(subject, subject_lw, query, query_lw).score;

        //Init
        let score_row = new Array(qlen);
        let csc_row = new Array(qlen);

        // Directions constants
        let STOP = 0;
        let UP = 1;
        let LEFT = 2;
        let DIAGONAL = 3;

        //Traceback matrix
        let trace = new Array(slen * qlen);
        let pos = -1;

        //Fill with 0
        let qindex = -1; //0..n-1
        while (++qindex < qlen) {
            score_row[qindex] = 0;
            csc_row[qindex] = 0;
        }

        let sindex = -1; //0..slen-1

        let move;

        while (++sindex < slen) {
            //foreach char si of subject
            let score = 0;
            let score_up = 0;
            let csc_diag = 0;
            let si_lw = subject_lw[sindex];

            let qindex = -1; // 0..qlen-1
            while (++qindex < qlen) {
                //foreach char qj of query
                //reset score
                let csc_score = 0;
                let align = 0;
                let score_diag = score_up;

                //Compute a tentative match
                if (query_lw[qindex] === si_lw) {
                    let start = Scorer.isWordStart(sindex, subject, subject_lw);

                    // Forward search for a sequence of consecutive char
                    csc_score = csc_diag > 0 ? csc_diag : Scorer.scoreConsecutives(subject, subject_lw, query, query_lw, sindex, qindex, start);

                    // Determine bonus for matching A[i] with B[j]
                    align = score_diag + Scorer.scoreCharacter(sindex, start, acro_score, csc_score);
                }

                //Prepare next sequence & match score.
                score_up = score_row[qindex]; // Current score_up is next run score diag
                csc_diag = csc_row[qindex];

                //In case of equality, moving UP get us closer to the start of the candidate string.
                if (score > score_up) {
                    move = LEFT;
                } else {
                    score = score_up;
                    move = UP;
                }

                // Only take alignment if it's the absolute best option.
                if (align > score) {
                    score = align;
                    move = DIAGONAL;
                } else {
                    //If we do not take this character, break consecutive sequence.
                    // (when consecutive is 0, it'll be recomputed)
                    csc_score = 0;
                }

                score_row[qindex] = score;
                csc_row[qindex] = csc_score;
                trace[++pos] = score > 0 ? move : STOP;
            }
        }

        // -------------------
        // Go back in the trace matrix
        // and collect matches (diagonals)

        sindex = slen - 1;
        qindex = qlen - 1;
        pos = sindex * qlen + qindex;
        let backtrack = true;
        let matches = [];

        while (backtrack && sindex >= 0 && qindex >= 0) {
            switch (trace[pos]) {
                case UP:
                    sindex--;
                    pos -= qlen;
                    break;

                case LEFT:
                    qindex--;
                    pos--;
                    break;

                case DIAGONAL:
                    matches.push(sindex + offset);
                    qindex--;
                    sindex--;
                    pos -= qlen + 1;
                    break;
                default:
                    backtrack = false;
            }
        }

        matches.reverse();
        return matches;
    }
}
